/**
 * The confirm step for a file that is genuinely shared between residents (2026-10-15).
 *
 * Talhia Zalvidea, filing one BOM vetting recording for the four residents of one building: "it says to confirm with
 * a reason but there is nowhere to put the reason." The upload guard (#416) refuses the same bytes on a second
 * resident with DOCUMENT_BELONGS_TO_ANOTHER_RESIDENT; for a kind that may be shared the refusal's `detail.confirm`
 * says what a confirmed upload sends (`override_reason`, its length, and a preset for a vetting call). Every upload
 * form that can meet that refusal asks for the reason with this one control, so they cannot disagree.
 *
 * Never for an audiogram, a UMED or a NOCA: the server refuses those outright (`overridable: false`) and no confirm
 * is drawn.
 */
import React from 'react';

/** What the refusal said about confirming, read defensively (an older server sends no `confirm`). */
export interface SharedRefusal {
  overridable: boolean;
  /** A sensible reason to offer with one click (a vetting call or its recording), or null. */
  preset: string | null;
  /** The kind may be filed for a whole building at once. */
  facilityWide: boolean;
  minLength: number;
  maxLength: number;
}

export const FACILITY_WIDE_REASON = 'Facility-wide recording (one call covers every resident)';
const HARD = ['UMED', 'AUDIOGRAM', 'NOCA'];
const SHAREABLE = ['BOM_VETTING', 'RECORDING'];

export function readSharedRefusal(detail: any, documentClass: string): SharedRefusal {
  const d = detail && typeof detail === 'object' ? detail : {};
  const overridable = typeof d.overridable === 'boolean' ? d.overridable : !HARD.includes(documentClass);
  const c = d.confirm && typeof d.confirm === 'object' ? d.confirm : {};
  const facilityWide = typeof d.facilityWide === 'boolean' ? d.facilityWide : SHAREABLE.includes(documentClass);
  return {
    overridable,
    preset: overridable ? (typeof c.preset === 'string' ? c.preset : c.preset === null ? null : facilityWide ? FACILITY_WIDE_REASON : null) : null,
    facilityWide: overridable && facilityWide,
    minLength: Number.isFinite(c.minLength) ? c.minLength : 3,
    maxLength: Number.isFinite(c.maxLength) ? c.maxLength : 500,
  };
}

/**
 * The reason field: required, free text, with the preset one click away. `tone` picks the workspace's class prefix.
 * The input keeps the label "Why this file is shared" so every form names it the same way.
 */
export default function SharedFileReason({ value, onChange, preset, maxLength = 500, tone, label = 'Why is this file shared?' }: {
  value: string; onChange: (v: string) => void; preset: string | null; maxLength?: number; tone: 'pa' | 'um'; label?: string;
}) {
  return (
    <div className={`${tone}-shared-reason`}>
      <label><span>{label}</span>
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} maxLength={maxLength} required aria-label="Why this file is shared" />
      </label>
      {preset && value.trim() !== preset && (
        <button type="button" className={tone === 'pa' ? 'pa-inline-edit' : 'um-link'} onClick={() => onChange(preset)}>Use: {preset}</button>
      )}
    </div>
  );
}
